import type {
  AllianceUserRoleVo,
  AllianceUserVo,
  MenuTreeVo,
} from "types/openapi/auth";
import { getJoinAllianceList } from "@/api/alliance";
import { defineStore } from "pinia";
import { getMenuList } from "@/api/app";
import { getUnionRoles, getUserInfo } from "@/api/user";
import { filterMenus } from "@/helper/router";
import { flattenTree } from "@/utils";
import router from "@/router";
import { useAppStore } from "./app";
import { useCommonStore } from "./common";

interface IPermissionStore {
  menuList: any[];
  menuMap: Map<string, MenuTreeVo & { crumb?: any[] }>;
  userRole: AllianceUserRoleVo;
  userDetail: AllianceUserVo;
  expandedMenuKeys: string[];
}

export const usePermissionStore = defineStore("permission", () => {
  const store: IPermissionStore = reactive({
    menuList: [],
    menuMap: new Map(),
    userRole: {},
    userDetail: {},
    expandedMenuKeys: [],
  });
  const isAdmin = computed(() => {
    return store.userRole?.admin;
  });
  async function genMenuList() {
    // 第一批：5 个互不依赖的请求并行
    const [menuListRes, userInfoRes, , , allianceListRes] = await Promise.all([
      getMenuList(),
      getUserInfo(),
      useCommonStore().getUnitList(),
      useCommonStore().GetCategoryTree(true),
      getJoinAllianceList({ pageNum: 1, pageSize: 100, sortJoinTime: 0 }),
    ]);

    const menuList = menuListRes?.data?.data?.menuList || [];
    store.menuList = filterMenus(menuList);
    store.userDetail = userInfoRes?.data?.data || {};

    // 恢复联盟
    const appStore = useAppStore();
    appStore.allianceList = allianceListRes?.data?.data?.data || [];
    if (appStore.allianceList?.length > 0) {
      restoreAlliance(appStore.allianceList);
    }

    // 第二批：依赖 allianceCode 的请求
    const unionRolesRes = await getUnionRoles({
      allianceCode: appStore.alliance?.allianceCode,
    });
    store.userRole = unionRolesRes?.data?.data || {};

    const menuMap = new Map<string, MenuTreeVo & { crumb?: any[] }>();
    flattenTree<MenuTreeVo>(store.menuList).forEach((item) => {
      menuMap.set(item.path as string, item);
    });
    store.menuMap = menuMap;
  }

  /** 从列表中恢复联盟：优先已保存的 allianceCode，找不到则取第一个 */
  function restoreAlliance(list: any[]) {
    const app = useAppStore();
    const saved = app.alliance?.allianceCode;
    const match = list.find((a: any) => a.allianceCode === saved);
    app.alliance = match || list[0] || null;
  }

  async function switchAlliance(alliance: any) {
    const appStore = useAppStore();
    appStore.alliance = alliance;
    const unionRolesRes = await getUnionRoles({
      allianceCode: alliance.allianceCode,
    });
    const menuListRes = await getMenuList();
    const userInfoRes = await getUserInfo();
    const menuList = menuListRes?.data?.data?.menuList || [];
    store.menuList = filterMenus(menuList);
    store.userRole = unionRolesRes?.data?.data || {};
    store.userDetail = userInfoRes?.data?.data || {};
    const menuMap = new Map<string, MenuTreeVo & { crumb?: any[] }>();
    flattenTree<MenuTreeVo>(store.menuList).forEach((item) => {
      menuMap.set(item.path as string, item);
    });
    store.menuMap = menuMap;
    const firstPath =
      store.menuList[0]?.children?.[0]?.path || store.menuList[0]?.path;
    if (firstPath) {
      if (router.currentRoute.value.path === firstPath) {
        await router.replace({ path: "/redirect" + firstPath });
      } else {
        router.push(firstPath);
      }
    }
  }
  function clear() {
    store.menuList = [];
    store.menuMap = new Map();
    store.userRole = {};
    store.userDetail = {};
  }
  return {
    ...toRefs(store),
    genMenuList,
    switchAlliance,
    clear,
    isAdmin,
  };
});
